import React from 'react';
import HeroSection from '../components/shared/HeroSection';
import { heroImages } from '../data/mock';
import { ExternalLink, Wrench, Recycle, TreePine, Leaf, ChevronRight } from 'lucide-react';

const EvenementsPage = () => {
  return (
    <div className="min-h-screen">
      <HeroSection
        image={heroImages.evenements}
        title="Foulées Vertes & Co"
        subtitle="Nos engagements et partenaires pour un sport plus responsable"
        height="h-[50vh]"
      />

      {/* Section 1: Goodloop - Réparation textile */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
              <Wrench className="w-6 h-6 text-emerald-700" />
            </div>
            <h2 className="text-2xl md:text-3xl font-neuropolitical font-bold text-gray-900">
              Réparation textile avec Goodloop
            </h2>
          </div>

          <div className="space-y-4 text-gray-600 leading-relaxed text-justify">
            <p>
              Avec <strong>Goodloop</strong>, spécialiste de la réparation de vêtements et équipements techniques de sport, nous vous proposons de prolonger la vie de votre équipement.
            </p>
            <p>
              Vestes imperméables, doudounes, sacs à dos ou équipements outdoor : chaque réparation est réalisée en France avec des techniques adaptées pour préserver performance et étanchéité.
            </p>
            <p>
              En choisissant la réparation, vous faites un geste à la fois économique et écologique, tout en continuant à profiter pleinement de votre équipement. Moins de déchet, plus de kilomètres !
            </p>
            <p className="font-medium text-gray-800">
              Pour profiter des services Goodloop rien de plus simple :
            </p>
          </div>

          <div className="mt-8 flex flex-col items-start gap-6">
            <img
              src="/assets/images/partners/goodloop.png"
              alt="Goodloop"
              className="h-28 w-auto object-contain"
              loading="lazy"
              decoding="async"
              data-testid="goodloop-logo"
            />
            <a
              href="https://goodloop.fr/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-700 text-white rounded-lg hover:bg-emerald-800 transition-colors font-medium"
              data-testid="goodloop-link"
            >
              Plus d'info sur goodloop.fr
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-5xl mx-auto px-4">
        <div className="border-t border-gray-200" />
      </div>

      {/* Section 2: RunCollect - Seconde vie chaussures */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
              <Recycle className="w-6 h-6 text-emerald-700" />
            </div>
            <h2 className="text-2xl md:text-3xl font-neuropolitical font-bold text-gray-900">
              Donner une seconde vie à vos chaussures
            </h2>
          </div>

          <div className="space-y-4 text-gray-600 leading-relaxed text-justify">
            <p>
              Nous travaillons également avec <strong>RunCollect</strong>, une initiative dédiée à la revalorisation des chaussures de running usagées.
            </p>
            <p>
              Le principe est simple : vous ramenez vos anciennes paires en magasin, et elles sont soit redistribuées à des associations, soit recyclées si elles sont trop usées.
            </p>
            <p>
              L'objectif : encourager une pratique du running plus responsable, en donnant une seconde vie à votre équipement et en soutenant des actions solidaires.
            </p>
          </div>

          <div className="mt-8 flex flex-col items-start gap-6">
            <img
              src="/assets/images/partners/runcollect.jpg"
              alt="RunCollect"
              className="h-16 w-auto object-contain"
              loading="lazy"
              decoding="async"
              data-testid="runcollect-logo"
            />
            <a
              href="https://runcollect.fr/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-700 text-white rounded-lg hover:bg-emerald-800 transition-colors font-medium"
              data-testid="runcollect-link"
            >
              Découvrir RunCollect
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-5xl mx-auto px-4">
        <div className="border-t border-gray-200" />
      </div>

      {/* Section 3: Trail Runner Foundation */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
              <TreePine className="w-6 h-6 text-emerald-700" />
            </div>
            <h2 className="text-2xl md:text-3xl font-neuropolitical font-bold text-gray-900">
              Courir utile, tout simplement
            </h2>
          </div>

          <div className="space-y-4 text-gray-600 leading-relaxed text-justify">
            <p>
              Parce que nos terrains de jeu sont aussi ce que nous avons de plus précieux, nous soutenons des initiatives qui agissent concrètement pour les préserver.
            </p>
            <p>
              Avec <strong>Trail Runner Foundation</strong>, chaque sortie devient une opportunité d'agir. Leur message est simple : respecter la nature, ne rien laisser derrière soi, et ramasser ce que l'on trouve sur son chemin.
            </p>
            <p>
              Née dans les Pyrénées de coureurs fatigués de voir des déchets sur les sentiers, l'association fédère aujourd'hui une large communauté autour des valeurs d'une pratique sportive plus respectueuse.
            </p>
            <p>
              Sur le terrain, cela se traduit par des actions concrètes :
            </p>
          </div>

          {/* Action points */}
          <div className="mt-6 space-y-3">
            <div className="flex items-center gap-3">
              <Leaf className="w-5 h-5 text-emerald-600 flex-shrink-0" />
              <span className="text-gray-700">Ramassage de déchets lors des sorties</span>
            </div>
            <div className="flex items-center gap-3">
              <Leaf className="w-5 h-5 text-emerald-600 flex-shrink-0" />
              <span className="text-gray-700">Sensibilisation des pratiquants</span>
            </div>
            <div className="flex items-center gap-3">
              <Leaf className="w-5 h-5 text-emerald-600 flex-shrink-0" />
              <span className="text-gray-700">Accompagnement des organisateurs pour rendre les courses plus responsables</span>
            </div>
          </div>

          <p className="mt-6 text-gray-600 leading-relaxed text-justify">
            De plus en plus d'événements s'engagent dans cette démarche, en adoptant des pratiques éco-responsables et en intégrant cette vision du sport outdoor.
          </p>

          <div className="mt-8 flex flex-col items-start gap-6">
            <img
              src="/assets/images/partners/trf.png"
              alt="Trail Runner Foundation"
              className="h-16 w-auto object-contain"
              loading="lazy"
              decoding="async"
              data-testid="trf-logo"
            />
            <a
              href="https://www.trailrunnerfoundation.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-700 text-white rounded-lg hover:bg-emerald-800 transition-colors font-medium"
              data-testid="trail-runner-foundation-link"
            >
              Découvrir Trail Runner Foundation
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EvenementsPage;
